import { useSyncExternalStore } from 'react'

/**
 * L'envoi **direct** d'une vidéo au stockage, par l'URL que l'API a signée (#418), et son
 * avancement, que la vignette de la pièce affiche.
 *
 * ⚠️ `XMLHttpRequest` et non `fetch` : seul lui rend l'avancement d'un envoi (`upload.onprogress`).
 * Une vidéo de 400 Mo met des minutes à monter sur la 4G, et l'agent doit voir qu'elle avance.
 *
 * ⚠️ Un refus du stockage (URL expirée, `403`) ou une coupure lève une `Error` simple, jamais un
 * `ApiError` : la file la lit comme une panne passagère, et redemande une URL au tour suivant.
 */
export function putSigned(
  url: string,
  blob: Blob,
  onProgress: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('PUT', url)
    // Le type signé avec l'URL : un autre ferait refuser l'envoi par le stockage.
    request.setRequestHeader('Content-Type', blob.type.toLowerCase())
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total)
    }
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) resolve()
      else reject(new Error(`le stockage a refusé la vidéo (${String(request.status)})`))
    }
    request.onerror = () => reject(new Error('stockage injoignable pendant l’envoi de la vidéo'))
    request.send(blob)
  })
}

// ponytail: en mémoire du seul onglet qui envoie — un autre onglet ne voit pas l'avancement.
const progress = new Map<string, number>()
const listeners = new Set<() => void>()

/** Pose l'avancement d'une pièce, entre 0 et 1, ou le retire (`null`) quand l'envoi s'arrête. */
export function setUploadProgress(mediumId: string, fraction: number | null): void {
  if (fraction === null) progress.delete(mediumId)
  else progress.set(mediumId, fraction)

  for (const listener of listeners) listener()
}

/** L'avancement d'une pièce en cours d'envoi direct, ou `null`. */
export function uploadProgressOf(mediumId: string): number | null {
  return progress.get(mediumId) ?? null
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)

  return () => listeners.delete(listener)
}

/** L'avancement d'une pièce, relu à chaque progrès de l'envoi. */
export function useUploadProgress(mediumId: string): number | null {
  return useSyncExternalStore(subscribe, () => uploadProgressOf(mediumId))
}
